import React, {useEffect, useRef, useState} from "react";
import {inject, observer} from "mobx-react";
import CreatableSelect from "react-select/creatable/dist/react-select.esm";
import Swal from "sweetalert2";
import moment from "moment";
import configuration from "../../../configuration/configuration";
import Pagination from "../../../components/Akitatek/Pagination";
import styles from "./Mediatheque.module.css";

/**
 * Médiathèque : une seule grille pour voir, envoyer, ranger et supprimer les images de la galerie.
 *
 * - mode « gerer » (page Images) : un clic ouvre la fiche à droite (nom, tags, « utilisée dans »...), la case à cocher
 *   sélectionne plusieurs images pour leur ajouter des tags ou les supprimer ;
 * - mode « choisir » (fenêtre de choix d'une image dans un produit, un article, un fond...) : un clic choisit l'image.
 *
 * Dans les deux modes on peut déposer des fichiers n'importe où sur la médiathèque pour les envoyer.
 */

const echapper = texte => String(texte ?? "").replace(/[&<>"']/g, c => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
}[c]));

const TAGS_VISIBLES = 12;

function Mediatheque({rootStore, mode = "gerer", onChoisir, idOuvert}) {
    const store = rootStore.imageStore;
    const choisir = mode === "choisir";

    const [recherche, setRecherche] = useState(store.filter.search ?? "");
    const [selection, setSelection] = useState([]);
    const [ouverte, setOuverte] = useState(null);
    const [glisse, setGlisse] = useState(false);
    const [envoi, setEnvoi] = useState(null);
    const [tousLesTags, setTousLesTags] = useState(false);
    const [tagsEnMasse, setTagsEnMasse] = useState([]);
    const champFichiers = useRef(null);
    const profondeurGlisse = useRef(0);

    useEffect(() => {
        store.filter.page = 1;
        store.getDatas();
        store.getAllTags();
        store.chargerTagsDesImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (idOuvert) {
            ouvrir(idOuvert);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idOuvert]);

    // La recherche part un court instant après la dernière touche
    useEffect(() => {
        if (recherche === (store.filter.search ?? "")) {
            return;
        }
        const minuteur = setTimeout(() => filtrer({search: recherche}), 300);
        return () => clearTimeout(minuteur);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recherche]);

    function filtrer(changements) {
        Object.assign(store.filter, changements, {page: 1});
        store.getDatas();
    }

    async function ouvrir(id) {
        const fiche = await store.fiche(id);
        if (fiche) {
            setOuverte(fiche);
        }
    }

    function basculerSelection(id) {
        setSelection(selection.includes(id) ? selection.filter(s => s !== id) : [...selection, id]);
    }

    function cliquer(e, image) {
        if (choisir) {
            onChoisir && onChoisir(image);
            return;
        }
        if (e.shiftKey || e.metaKey || e.ctrlKey || selection.length > 0) {
            // en mode sélection, un clic coche ou décoche au lieu d'ouvrir la fiche
            basculerSelection(image.id);
            return;
        }
        ouvrir(image.id);
    }

    async function envoyer(fichiers) {
        const images = [...fichiers].filter(f => f.type.startsWith("image/"));
        if (images.length === 0) {
            rootStore.notificationStore.setNotification("Seules les images peuvent être ajoutées ici.", "error");
            return;
        }
        setEnvoi({nombre: images.length, progression: 0});
        const ajoutees = await store.envoyer(images, progression => setEnvoi({nombre: images.length, progression}));
        setEnvoi(null);
        store.chargerTagsDesImages();

        if (ajoutees.length === 1) {
            if (choisir) {
                onChoisir && onChoisir(ajoutees[0]);
            } else {
                ouvrir(ajoutees[0].id);
            }
        }
    }

    const glisser = {
        onDragEnter: e => {
            if (!e.dataTransfer?.types?.includes("Files")) return;
            e.preventDefault();
            profondeurGlisse.current++;
            setGlisse(true);
        },
        onDragOver: e => {
            if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
        },
        onDragLeave: () => {
            profondeurGlisse.current = Math.max(0, profondeurGlisse.current - 1);
            if (profondeurGlisse.current === 0) setGlisse(false);
        },
        onDrop: e => {
            if (!e.dataTransfer?.files?.length) return;
            e.preventDefault();
            profondeurGlisse.current = 0;
            setGlisse(false);
            envoyer(e.dataTransfer.files);
        }
    };

    async function supprimer(ids) {
        const fiches = (await Promise.all(ids.map(id => store.fiche(id)))).filter(Boolean);
        const utilisees = fiches.filter(f => f.utilisations.length > 0);

        let html = "Impossible de revenir en arrière.";
        if (utilisees.length > 0) {
            const lignes = utilisees.map(f => {
                const endroits = f.utilisations.map(u => echapper(u.quoi) + (u.nom ? " « " + echapper(u.nom) + " »" : "")).join(", ");
                return "<li><b>" + echapper(f.nom) + "</b> : " + endroits + "</li>";
            }).join("");
            html = "<p>" + (utilisees.length > 1 ? "Ces images sont encore utilisées" : "Cette image est encore utilisée") +
                ". Ces endroits n'auront plus d'image :</p><ul class='" + styles.listeUtilisations + "'>" + lignes + "</ul>";
        }

        const reponse = await Swal.fire({
            title: ids.length > 1 ? "Supprimer " + ids.length + " images ?" : "Supprimer « " + (fiches[0]?.nom ?? "cette image") + " » ?",
            html,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Supprimer",
            cancelButtonText: "Annuler",
            reverseButtons: true
        });
        if (!reponse.value) {
            return;
        }

        await store.supprimer(ids);
        setSelection(selection.filter(id => !ids.includes(id)));
        if (ouverte && ids.includes(ouverte.id)) {
            setOuverte(null);
        }
        store.chargerTagsDesImages();
    }

    async function ajouterTagsEnMasse() {
        const tags = await store.idsDesTags(tagsEnMasse);
        if (tags.length === 0) return;
        if (await store.ajouterTags(selection, tags)) {
            setTagsEnMasse([]);
            setSelection([]);
            store.chargerTagsDesImages();
        }
    }

    function copier(image) {
        const lien = configuration.PhotoBase + image.filename;
        navigator.clipboard.writeText(lien);
        rootStore.notificationStore.setNotification("Lien copié", "success");
    }

    const images = store.datas ?? [];
    const tagsAffiches = tousLesTags ? store.tagsDesImages : store.tagsDesImages.slice(0, TAGS_VISIBLES);
    const tagActif = String(store.filter.tag ?? "");
    const nonUtilisees = !!store.filter.non_utilisees;
    const optionsTags = (store.tags ?? []).map(t => ({label: t.nom, value: t.id}));

    return (
        <div className={styles.mediatheque} {...glisser}>
            {glisse && (
                <div className={styles.zoneDepot}>
                    <i className="fa fa-cloud-upload"/> Déposez vos images ici
                </div>
            )}

            <div className={styles.barre}>
                <div className={styles.recherche}>
                    <i className="fa fa-search"/>
                    <input type="search"
                           placeholder="Rechercher un nom ou un tag"
                           value={recherche}
                           onChange={e => setRecherche(e.target.value)}/>
                </div>
                <label className={styles.interrupteur}>
                    <input type="checkbox" checked={nonUtilisees}
                           onChange={() => filtrer({non_utilisees: nonUtilisees ? "" : "1"})}/>
                    Non utilisées
                </label>
                <span className={styles.total}>{store.total ?? 0} image{store.total > 1 ? "s" : ""}</span>
                <button type="button" className="btn btn-success" onClick={() => champFichiers.current.click()}>
                    <i className="fa fa-plus mr-1"/> Ajouter des images
                </button>
                <input ref={champFichiers} type="file" accept="image/*" multiple hidden
                       onChange={e => {
                           envoyer(e.target.files);
                           e.target.value = "";
                       }}/>
            </div>

            {store.tagsDesImages.length > 0 && (
                <div className={styles.tags}>
                    <button type="button"
                            className={tagActif === "" ? styles.tagActif : styles.tag}
                            onClick={() => filtrer({tag: ""})}>Tous</button>
                    {tagsAffiches.map(tag => (
                        <button type="button" key={tag.id}
                                className={tagActif === String(tag.id) ? styles.tagActif : styles.tag}
                                onClick={() => filtrer({tag: tagActif === String(tag.id) ? "" : tag.id})}>
                            #{tag.nom} <span className={styles.nombre}>{tag.assets_count}</span>
                        </button>
                    ))}
                    {store.tagsDesImages.length > TAGS_VISIBLES && (
                        <button type="button" className={styles.tagPlus} onClick={() => setTousLesTags(!tousLesTags)}>
                            {tousLesTags ? "Moins" : "+ " + (store.tagsDesImages.length - TAGS_VISIBLES) + " tags"}
                        </button>
                    )}
                </div>
            )}

            {envoi && (
                <div className={styles.envoi}>
                    Envoi de {envoi.nombre} image{envoi.nombre > 1 ? "s" : ""}… {envoi.progression}%
                    <div className={styles.progression}><div style={{width: envoi.progression + "%"}}/></div>
                </div>
            )}

            {!choisir && selection.length > 0 && (
                <div className={styles.barreSelection}>
                    <b>{selection.length} sélectionnée{selection.length > 1 ? "s" : ""}</b>
                    <div className={styles.champTags}>
                        <CreatableSelect isMulti
                                         placeholder="Ajouter des tags…"
                                         formatCreateLabel={v => "Créer le tag « " + v + " »"}
                                         value={tagsEnMasse}
                                         options={optionsTags}
                                         onChange={v => setTagsEnMasse(v ?? [])}/>
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" disabled={tagsEnMasse.length === 0}
                            onClick={ajouterTagsEnMasse}>Ajouter</button>
                    <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => setSelection([...new Set([...selection, ...images.map(i => i.id)])])}>
                        Toute la page
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => supprimer(selection)}>
                        <i className="fa fa-trash"/> Supprimer
                    </button>
                    <button type="button" className="btn btn-link btn-sm" onClick={() => setSelection([])}>Annuler</button>
                </div>
            )}

            <div className={!choisir && ouverte ? styles.ficheOuverte : ""}>
                <div className={styles.colonneGrille}>
                    {images.length === 0 ? (
                        <div className={styles.vide}>
                            {store.filter.search || store.filter.tag || nonUtilisees
                                ? "Aucune image ne correspond."
                                : "Aucune image. Glissez-en ici ou cliquez sur « Ajouter des images »."}
                        </div>
                    ) : (
                        <div className={styles.grille + (selection.length > 0 ? " " + styles.enSelection : "")}>
                            {images.map(image => {
                                const selectionnee = selection.includes(image.id);
                                const active = ouverte?.id === image.id;
                                return (
                                    <div key={image.id}
                                         className={[styles.carte, selectionnee ? styles.selectionnee : "", active ? styles.active : ""].join(" ")}
                                         title={choisir ? "Choisir « " + image.nom + " »" : image.nom}
                                         onClick={e => cliquer(e, image)}>
                                        {!choisir && (
                                            <input type="checkbox"
                                                   className={styles.caseACocher}
                                                   checked={selectionnee}
                                                   aria-label={"Sélectionner " + image.nom}
                                                   onClick={e => e.stopPropagation()}
                                                   onChange={() => basculerSelection(image.id)}/>
                                        )}
                                        <div className={styles.vignette}>
                                            <img loading="lazy" alt={image.description || image.nom}
                                                 src={configuration.PhotoBase + image.filename}/>
                                        </div>
                                        <div className={styles.nom}>{image.nom}</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {store.filter.last_page > 1 && (
                        <Pagination currentPage={store.filter.page}
                                    lastPage={store.filter.last_page}
                                    parentCallback={page => {
                                        store.filter.page = page;
                                        store.getDatas();
                                    }}/>
                    )}
                </div>

                {!choisir && ouverte && (
                    <FicheImage key={ouverte.id}
                                rootStore={rootStore}
                                image={ouverte}
                                optionsTags={optionsTags}
                                onFermer={() => setOuverte(null)}
                                onEnregistree={image => setOuverte({...image, utilisations: ouverte.utilisations})}
                                onCopier={() => copier(ouverte)}
                                onSupprimer={() => supprimer([ouverte.id])}/>
                )}
            </div>
        </div>
    );
}

const FicheImage = observer(function FicheImage({rootStore, image, optionsTags, onFermer, onEnregistree, onCopier, onSupprimer}) {
    const store = rootStore.imageStore;
    const [nom, setNom] = useState(image.nom ?? "");
    const [description, setDescription] = useState(image.description ?? "");
    const [tags, setTags] = useState((image.tags ?? []).map(t => ({label: t.nom, value: t.id})));
    const [fichier, setFichier] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const lien = configuration.PhotoBase + image.filename;

    async function enregistrer(e) {
        e.preventDefault();
        setEnCours(true);
        const enregistree = await store.modifier({
            id: image.id,
            nom,
            description,
            tags: await store.idsDesTags(tags),
            image: fichier
        });
        setEnCours(false);
        if (enregistree) {
            setFichier(null);
            onEnregistree(enregistree);
            store.chargerTagsDesImages();
        }
    }

    return (
        <aside className={styles.fiche}>
            <div className={styles.ficheEntete}>
                <b>Image #{image.id}</b>
                <button type="button" className="close" aria-label="Fermer" onClick={onFermer}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            <a href={lien} target="_blank" rel="noopener noreferrer" className={styles.apercu} title="Ouvrir en grand">
                <img src={fichier ? URL.createObjectURL(fichier) : lien} alt={image.description || image.nom}/>
            </a>

            <form onSubmit={enregistrer}>
                <label className={styles.etiquette}>Nom</label>
                <input className="form-control form-control-sm" required value={nom} onChange={e => setNom(e.target.value)}/>

                {rootStore.authStore.options?.metadata === 1 && (
                    <>
                        <label className={styles.etiquette}>Description (texte alternatif)</label>
                        <input className="form-control form-control-sm" value={description}
                               onChange={e => setDescription(e.target.value)}/>
                    </>
                )}

                <label className={styles.etiquette}>Tags</label>
                <CreatableSelect isMulti
                                 placeholder="Aucun tag"
                                 formatCreateLabel={v => "Créer le tag « " + v + " »"}
                                 value={tags}
                                 options={optionsTags}
                                 onChange={v => setTags(v ?? [])}/>

                <label className={styles.etiquette}>Remplacer le fichier</label>
                <input type="file" accept="image/*" className="form-control-file"
                       onChange={e => setFichier(e.target.files[0] ?? null)}/>

                <div className={styles.ficheBoutons}>
                    <button type="submit" className="btn btn-success btn-sm" disabled={enCours}>Enregistrer</button>
                    <button type="button" className="btn btn-outline-primary btn-sm" onClick={onCopier}>
                        <i className="fa fa-copy"/> Copier le lien
                    </button>
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={onSupprimer}>
                        <i className="fa fa-trash"/> Supprimer
                    </button>
                </div>
            </form>

            <label className={styles.etiquette}>Utilisée dans</label>
            {image.utilisations?.length > 0 ? (
                <ul className={styles.utilisations}>
                    {image.utilisations.map((u, i) => (
                        <li key={i}>{u.quoi}{u.nom ? <> : <b>{u.nom}</b></> : ""}</li>
                    ))}
                </ul>
            ) : (
                <div className={styles.libre}>Utilisée nulle part : vous pouvez la supprimer sans risque.</div>
            )}

            <div className={styles.details}>
                {image.filename}
                {image.created_at && <> · ajoutée le {moment(image.created_at).format("DD/MM/YYYY")}</>}
            </div>
        </aside>
    );
});

export default inject("rootStore")(observer(Mediatheque));
